import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, User, LogOut, Save, Eye, EyeOff, Database } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import PopulateDataButton from '../../components/admin/PopulateDataButton';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

const AdminSettings: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { logout, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    name: user?.name || 'Admin User',
    email: user?.email || 'admin@bookfood.com'
  });

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const handleLogout = async () => {
    try {
      // Clear admin session
      await logout();
      // Redirect to welcome screen instead of login
      setCurrentScreen('welcome');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to welcome screen
      setCurrentScreen('welcome');
    }
  };

  const handleSave = () => {
    // In a real app, this would save to backend
    console.log('Settings saved:', settings);
    // Show success message
    alert('Settings saved successfully!');
  };

  const handleInputChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const SettingItem: React.FC<{ 
    icon: React.ReactNode; 
    title: string; 
    description: string; 
    children: React.ReactNode 
  }> = ({ icon, title, description, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-800 rounded-xl p-4 border border-slate-700"
    >
      <div className="flex items-center space-x-3 mb-3">
        <div className="p-2 bg-slate-700 rounded-lg">
          {icon}
        </div>
        <div>
          <h3 className="text-white font-semibold">{title}</h3>
          <p className="text-slate-400 text-sm">{description}</p>
        </div>
      </div>
      {children}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center p-6 bg-slate-800 border-b border-slate-700">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="p-2 rounded-full text-slate-300 hover:bg-slate-700 mr-4"
        >
          <ArrowLeft size={20} />
        </motion.button>
        <h1 className="text-xl font-bold text-white">Settings</h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Profile Settings */}
        <SettingItem
          icon={<User size={20} className="text-teal-400" />}
          title="Profile Information"
          description="Update your personal details"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
              <input
                type="text"
                value={settings.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Enter your email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Change Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter new password"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>
        </SettingItem>

        {/* Database Management */}
        <SettingItem
          icon={<Database size={20} className="text-blue-400" />}
          title="Database Management"
          description="Populate or reset Firebase data"
        >
          <PopulateDataButton />
        </SettingItem>

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <motion.button
            onClick={handleSave}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <Save size={20} />
            <span>Save Changes</span>
          </motion.button>
          <motion.button
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </motion.button>
        </div>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800 rounded-xl p-4 border border-slate-700 text-center"
        >
          <h3 className="text-white font-semibold mb-2">BookFood Admin</h3>
          <p className="text-slate-400 text-sm">Version 1.0.0</p>
          <p className="text-slate-500 text-xs mt-2">© 2024 BookFood. All rights reserved.</p>
        </motion.div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default AdminSettings;
